import React from "react";
import { motion } from "framer-motion";
import { HOME } from "@/constants/testIds";

// Highlight section for the "Carbon Trader" training program.
// Priced per client: €8.947 (−80%) → €1.789,40 una tantum, 3× €596,47 PayPal, +€97/mese.
export const CarbonTrader = () => {
  const scrollToForm = () => {
    document.getElementById("iscriviti")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="carbon-trader"
      data-testid={HOME.courseSection}
      className="section-dark relative py-24 sm:py-32 px-6 border-t border-white/10 overflow-hidden"
    >
      {/* Subtle backdrop grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse at 50% 40%, black 30%, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-[1200px]">
        {/* Header */}
        <div className="text-center mb-14 sm:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-[color:var(--lif-accent)]/40 bg-[color:var(--lif-accent)]/5 rounded-full px-4 py-1.5"
          >
            <span className="pulse-dot" />
            <span
              data-testid={HOME.courseAvailability}
              className="font-mono text-[11px] tracking-[0.3em] uppercase text-[color:var(--lif-accent)]"
            >
              Accesso prioritario
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="eyebrow mt-6"
          >
            Il percorso di punta LIF
          </motion.p>

          <motion.h2
            data-testid={HOME.courseTitle}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-4 font-display font-bold text-white leading-[0.9]"
            style={{ fontSize: "clamp(56px, 10vw, 160px)" }}
          >
            Carbon{" "}
            <span className="text-[color:var(--lif-accent)]">Trader</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 font-body text-white/70 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed"
          >
            Percorso formativo da <strong className="text-white">zero a libero</strong>.
            Impara il metodo, applica gli strumenti, costruisci la tua rendita.
          </motion.p>
        </div>

        {/* Pricing — two tiers side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Tier 1 — Corso una tantum */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.75, delay: 0.1 }}
            className="relative border border-white/15 rounded-xl p-8 sm:p-10 bg-white/[0.02] overflow-hidden"
          >
            <div className="absolute top-0 right-0 bg-[color:var(--lif-accent)] text-[color:var(--lif-bg)] font-mono text-[10px] font-bold tracking-[0.24em] uppercase px-4 py-1.5 rounded-bl-xl">
              −80%
            </div>

            <p className="eyebrow">Step 01 · Il corso</p>
            <h3 className="mt-3 font-display font-bold text-white text-2xl sm:text-3xl">
              Accesso completo + 12 mesi H24
            </h3>

            <div className="mt-8">
              <div
                data-testid={HOME.coursePriceFull}
                className="font-mono text-white/40 text-base line-through"
              >
                €8.947
              </div>
              <div className="flex items-baseline gap-3 mt-1">
                <span
                  data-testid={HOME.coursePriceDiscount}
                  className="font-display font-bold text-white leading-none"
                  style={{ fontSize: "clamp(48px, 6vw, 80px)" }}
                >
                  €1.789
                </span>
                <span className="font-display font-bold text-white/60 text-2xl">
                  ,40
                </span>
              </div>
              <p className="mt-2 font-mono text-[11px] tracking-[0.22em] uppercase text-white/50">
                Una tantum · IVA inclusa · Sconto −80%
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <p
                data-testid={HOME.coursePriceInstallments}
                className="font-mono text-white/80 text-sm"
              >
                Include{" "}
                <strong className="text-white">12 mesi</strong>{" "}
                di gestione &amp; assistenza H24
              </p>
            </div>

            <ul className="mt-8 space-y-3">
              {[
                "Video-lezioni on-demand complete",
                "Strumenti e protocolli LIF applicati",
                "12 mesi di assistenza H24 inclusi",
                "Community privata degli studenti",
                "Aggiornamenti a vita del materiale",
              ].map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-3 font-body text-white/80 text-sm"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className="mt-1 flex-shrink-0 text-[color:var(--lif-accent)]"
                  >
                    <path
                      d="M2 7l3 3 7-7"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="square"
                    />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Tier 2 — Abbonamento mensile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.75, delay: 0.2 }}
            className="relative border border-white/15 rounded-xl p-8 sm:p-10 bg-white/[0.02]"
          >
            <p className="eyebrow">Step 02 · Dal 13° mese</p>
            <h3 className="mt-3 font-display font-bold text-white text-2xl sm:text-3xl">
              Gestione & Assistenza H24
            </h3>

            <div className="mt-8">
              <div className="flex items-baseline gap-3">
                <span
                  data-testid={HOME.courseSubscription}
                  className="font-display font-bold text-white leading-none"
                  style={{ fontSize: "clamp(48px, 6vw, 80px)" }}
                >
                  €97
                </span>
                <span className="font-mono text-white/60 text-base tracking-[0.14em] uppercase">
                  / mese
                </span>
              </div>
              <p className="mt-2 font-mono text-[11px] tracking-[0.22em] uppercase text-white/50">
                Solo dopo i 12 mesi inclusi · Disdici quando vuoi
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="font-mono text-white/80 text-sm">
                Primo anno già <strong className="text-white">incluso</strong>{" "}
                nel corso. Il canone parte solo dal 13° mese.
              </p>
            </div>

            <ul className="mt-8 space-y-3">
              {[
                "Monitoraggio operativo h24",
                "Supporto tecnico diretto",
                "Dashboard di gestione personale",
                "Interventi rapidi in caso di criticità",
              ].map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-3 font-body text-white/80 text-sm"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className="mt-1 flex-shrink-0 text-[color:var(--lif-accent)]"
                  >
                    <path
                      d="M2 7l3 3 7-7"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="square"
                    />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* CTA block */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.75, delay: 0.3 }}
          className="mt-12 sm:mt-16 text-center max-w-xl mx-auto"
        >
          <p className="font-body text-white/70 text-base">
            I posti sono limitati. Iscriviti alla lista per avere accesso
            prioritario.
          </p>

          <button
            type="button"
            onClick={scrollToForm}
            data-testid={HOME.courseCta}
            className="lif-btn mt-6"
          >
            Prenota il tuo posto
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 6h8M6 2l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default CarbonTrader;
